import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {ApplicationEquipment, useNotification} from '@ozark/common';
import {EquipmentView, UserRoles} from '@ozark/functions/src/shared';
import {useCallback, useMemo, useState} from 'react';
import {TerminalsApi} from '../../../api';
import {useViewableEquipment} from './useViewableEquipment';

// On any logic change sync with ozark/functions/src/functions/express/private/services/terminalsService.ts
export const isEquipmentCreatedByAgent = (equipment: ApplicationEquipment, userId?: string) => {
  return equipment.isActive && equipment.isIntegration && equipment.agentId === userId;
};

export type VarSheetWizardProps = {
  open: boolean;
  onClose: () => void;
  role?: UserRoles;
  terminalsApi: TerminalsApi;
  appEquipment?: ApplicationEquipment;
  appEquipmentAdditional?: ApplicationEquipment[];
  applicationId: string;
  userId?: string;
};

export const VarSheetWizard = ({
  open,
  onClose,
  role,
  terminalsApi,
  appEquipment,
  appEquipmentAdditional,
  applicationId,
  userId,
}: VarSheetWizardProps) => {
  // Use form functionality with context and wizard component in case of any logic encomplication
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentView | null>(null);
  const [isHostCaptureSelected, setIsHostCaptureSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {equipment} = useViewableEquipment(role);
  const showNotification = useNotification();

  const isHostCaptureEnabled = selectedEquipment?.hostCapture;
  const varSheetLimitForAgent = 5;

  const isVarSheetLimitReached = useMemo(() => {
    if (!appEquipment && !appEquipmentAdditional) return false;

    const totalEquipment = [appEquipment, ...(appEquipmentAdditional || [])].filter(
      equipment => equipment && isEquipmentCreatedByAgent(equipment, userId)
    ).length;

    return totalEquipment >= varSheetLimitForAgent && role === UserRoles.agent;
  }, [appEquipment, appEquipmentAdditional, role, varSheetLimitForAgent]);

  const onGenerateVarSheet = useCallback(async () => {
    setIsSubmitting(true);
    if (!selectedEquipment) {
      return;
    }
    const res = await terminalsApi.addTerminal({
      equipmentId: selectedEquipment.id,
      applicationId: applicationId,
      isHostCapture: isHostCaptureSelected,
    });

    if (res.success) {
      onClose();
    } else {
      showNotification('error', res.message);
    }
    setIsSubmitting(false);
  }, [onClose, terminalsApi, selectedEquipment, isHostCaptureSelected]);

  const isGeneratingVarSheetEnabled = !!selectedEquipment && !isVarSheetLimitReached;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="create-dialog-title" maxWidth={'lg'}>
      <DialogTitle id="create-dialog-title">Var Sheet Wizard</DialogTitle>
      <DialogContent sx={{width: '50rem', maxWidth: '100%'}}>
        <Grid container spacing={2} sx={{padding: 2}}>
          <ListItem sx={{display: 'flex', justifyContent: 'space-between'}}>
            <Typography variant="body1">Select equipment:</Typography>
            <Autocomplete
              sx={{width: '100%'}}
              options={equipment.data || []}
              getOptionLabel={(x: EquipmentView) => x.name}
              onChange={(_, value) => setSelectedEquipment(value as EquipmentView)}
              renderInput={params => (
                <TextField {...params} name="equipment" label="Equipment" variant="outlined" />
              )}
            />
          </ListItem>
          {isHostCaptureEnabled && (
            <ListItem
              secondaryAction={
                <Switch
                  checked={isHostCaptureSelected}
                  onChange={() => setIsHostCaptureSelected(!isHostCaptureSelected)}
                />
              }
            >
              <ListItemText primary={'Host Capture'} />
            </ListItem>
          )}
          {isVarSheetLimitReached && (
            <Grid item xs={12}>
              <Alert severity="error">Please contact support.</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!isGeneratingVarSheetEnabled || isSubmitting}
          onClick={onGenerateVarSheet}
        >
          Generate Var Sheet
        </Button>
      </DialogActions>
    </Dialog>
  );
};
